import { create } from "zustand"
import {
	DEFAULT_NOTE_FONT_SIZE,
	DEFAULT_NOTE_TEXT_COLOR,
} from "components/ui/customWebviewer/interactions/note.constants"

// REQ-395/396: the note tool's armed state + the two style fields the tool-options bar exposes
// (font size, text color — "Font size and text color controls only" per the issue). Mirrors
// `figureHighlight.store.ts`'s shape (arm/disarm + the fields the toolbar shows), trimmed to the
// two fields a note actually has. Lives in a store, not component state, for the same reason
// `splitScreen.store` does: `CustomWebViewerHeader`'s Apryse `render()` callback captures its
// props once, so anything that changes after mount (the armed flag, the live style pickers, which
// note the bar is currently editing) must be read from a store instead of a prop.
type Store = {
	isArmed: boolean
	selectedFontSize: number
	selectedTextColor: string
	// Non-null while the bar's two fields reflect an EXISTING selected note rather than "next note"
	// defaults (Figma shows no distinct UI for this — the same bar doubles as both, per the issue's
	// "Edit & delete → Font size and text color controls only" requirement). Read by
	// `useNoteEdit` to decide whether a font-size/color change should PATCH that note instead of
	// only updating the next-note default.
	activeNoteId: number | null
	armNoteTool: () => void
	disarmNoteTool: () => void
	setSelectedFontSize: (size: number) => void
	setSelectedTextColor: (color: string) => void
	// Seeds the bar's two fields from an existing note's OWN values right after it is selected, so
	// the pickers show what the note actually has rather than the tool's last-used defaults.
	seedFromNote: (note: { id: number; font_size: number; text_color: string }) => void
	clearActiveNote: () => void
}

export const useNoteToolStore = create<Store>((set) => ({
	isArmed: false,
	selectedFontSize: DEFAULT_NOTE_FONT_SIZE,
	selectedTextColor: DEFAULT_NOTE_TEXT_COLOR,
	activeNoteId: null,
	armNoteTool: () => set({ isArmed: true }),
	disarmNoteTool: () => set({ isArmed: false }),
	setSelectedFontSize: (size) => set({ selectedFontSize: size }),
	setSelectedTextColor: (color) => set({ selectedTextColor: color }),
	seedFromNote: (note) =>
		set({ activeNoteId: note.id, selectedFontSize: note.font_size, selectedTextColor: note.text_color }),
	clearActiveNote: () => set({ activeNoteId: null }),
}))
